import logo from '@/assets/hiredcoach-logo.svg';
import Seo from '@/components/marketing/Seo';
import { useApiClient } from '@/lib/api';
import { getUserErrorMessage } from '@/lib/error-messages';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const ResetPassword: React.FC = () => {
  const { api } = useApiClient();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    let mounted = true;
    const verify = async () => {
      if (!token) {
        setVerifying(false);
        setValid(false);
        setError('Missing reset token. Please use the link from your email.');
        return;
      }
      try {
        await api.verifyPasswordResetToken(token);
        if (!mounted) return;
        setValid(true);
      } catch (e: any) {
        if (!mounted) return;
        setValid(false);
        setError('This reset link is invalid or has expired.');
      } finally {
        if (mounted) setVerifying(false);
      }
    };
    verify();
    return () => { mounted = false; };
  }, [token, api]);

  // When password reset succeeds, start a countdown and redirect to /login
  useEffect(() => {
    if (!success) return;
    setCountdown(5);
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(id);
          navigate('/login');
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [success, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.resetPassword(token, password);
      setSuccess(true);
    } catch (e: any) {
      console.error('Reset failed', e);
      setError(
        getUserErrorMessage(
          e,
          'Unable to reset password right now. Please try again later.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Reset Password | HiredCoach"
        description="Reset your HiredCoach password and get back to interview prep and job search practice."
        canonicalPath="/reset-password"
        robots="noindex,follow"
      />
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <img
          src={logo}
          alt="HiredCoach Logo"
          className="hidden md:block md:w-40 md:mx-auto md:my-12 md:flex-shrink-0"
        />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Set a new password</h1>
        <p className="text-slate-600 mb-6">Choose a strong password to secure your account.</p>

        {verifying && (
          <div className="text-slate-700">Verifying your reset link…</div>
        )}

        {!verifying && !valid && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 p-4">
            <div className="font-medium mb-1">This link isn’t valid</div>
            <p className="text-sm mb-3">{error || 'Your reset link is invalid or expired.'}</p>
            <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700 font-medium text-sm">Request a new link</Link>
          </div>
        )}

        {!verifying && valid && !success && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">New Password</label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter a new password"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-slate-700">Confirm Password</label>
              <input
                id="confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Re-enter your new password"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-white font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {submitting ? 'Updating…' : 'Update Password'}
            </button>
            <div className="text-sm text-slate-600 text-center">
              Remembered your password?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Back to login</Link>
            </div>
          </form>
        )}

        {success && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 p-4">
            <div className="font-medium mb-1">Password updated</div>
            <p className="text-sm">Your password has been reset successfully. You can now log in with your new password.</p>
            <p className="text-sm mt-2">Redirecting to login in {countdown}…</p>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-white font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Go to login ({countdown})
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
